import type { CategoryColorPickerHostProps } from './components/CategoryColorPickerHost';
import type { CategoryDialogProps } from './components/CategoryDialog';
import type { CategoryMenuProps } from './components/CategoryMenu';
import type { CategoryV2Props } from './components/CategoryV2';
import type { CategoryWellProps } from './components/CategoryWell';
import type * as lazyIndex from './lazyIndex';
/************************************************************************************
 * Note to future editors: static exports have been removed from this file and turned
 * into deep imports. This is a requirement for ongoing perf work.
 *
 * Please do not add a non-lazy export without contacting the Calendar Forms team:
 * zabryant, jareill, yingchon, or manager krsheth
 *
 * Adding an export may require updating both package.json and fence.json before use.
 * See the wiki for more info:
 * https://outlookweb.visualstudio.com/Outlook%20Web/_wiki/wikis/Outlook%20Web.wiki/12542/Bundle-Perf-Efforts
 ************************************************************************************/
import { LazyAction, type LazyComponent, LazyModule, createLazyComponent } from 'owa-bundling';

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "Categories" */ './lazyIndex'),
    { name: 'Categories' }
);

export const lazyAddMasterCategories: LazyAction<
    typeof lazyIndex.addMasterCategories,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.addMasterCategories);
export const lazyRemoveMasterCategories: LazyAction<
    typeof lazyIndex.removeMasterCategories,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.removeMasterCategories);
export const lazyRenameMasterCategories: LazyAction<
    typeof lazyIndex.renameMasterCategories,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.renameMasterCategories);
export const lazyChangeMasterCategories: LazyAction<
    typeof lazyIndex.changeMasterCategories,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.changeMasterCategories);
export const lazyUpdateLastTimeUsedMasterCategories: LazyAction<
    typeof lazyIndex.updateLastTimeUsedMasterCategories,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.updateLastTimeUsedMasterCategories);
export const lazySetInitialCategoryDialogViewState: LazyAction<
    typeof lazyIndex.setInitialCategoryDialogViewState,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setInitialCategoryDialogViewState);
export const lazyFetchCategoryDetails: LazyAction<
    typeof lazyIndex.fetchCategoryDetails,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.fetchCategoryDetails);
export const lazyRefreshMasterCategoryList: LazyAction<
    typeof lazyIndex.refreshMasterCategoryList,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.refreshMasterCategoryList);
export const lazySubscribeToCategoryNotifications: LazyAction<
    typeof lazyIndex.subscribeToCategoryNotifications,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.subscribeToCategoryNotifications);
export const lazyApplyCategoryOperationHelper: LazyAction<
    typeof lazyIndex.applyCategoryOperationHelper,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.applyCategoryOperationHelper);
export const lazyIsCategoryNameValid: LazyAction<
    typeof lazyIndex.isCategoryNameValid,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.isCategoryNameValid);
export const lazySetBlockedCategoryNames: LazyAction<
    typeof lazyIndex.setBlockedCategoryNames,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setBlockedCategoryNames);

export const lazyOnMenuDismissed: LazyAction<typeof lazyIndex.onMenuDismissed, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.onMenuDismissed);

export const CategoryMenu: LazyComponent<CategoryMenuProps, unknown, void> = createLazyComponent(
    lazyModule,
    m => m.CategoryMenu
);
export const CategoryDialog: LazyComponent<CategoryDialogProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.CategoryDialog);
export const CategoryColorPickerHost: LazyComponent<CategoryColorPickerHostProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.CategoryColorPickerHost);
export const CategoryWrapper: LazyComponent<CategoryV2Props, unknown, void> = createLazyComponent(
    lazyModule,
    m => m.CategoryWrapper
);
export const LazyCategoryWell: LazyComponent<CategoryWellProps, unknown, void> =
    createLazyComponent(lazyModule, m => m.CategoryWell);

export const lazyShowCategoryDialog: LazyAction<
    typeof lazyIndex.showCategoryDialog,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.showCategoryDialog);
